import { snakeCase } from "../../utils/changeCase"
import {
  uploadFileToS3,
  BaseUploadParams,
  DocumentUploadConfig,
  getApiClient,
} from "toptaal-shared"

interface UploadDocumentToS3Params {
  presignedUrl: string
  uniqueFilename: string
}
/**
 * Generic document upload function that handles the two-step upload process:
 * 1. POST to get presigned URL
 * 2. Upload file to S3
 * 3. PATCH to finalize the document
 */
export const uploadDocument = async <T extends BaseUploadParams>(
  params: T,
  config: DocumentUploadConfig,
) => {
  const { displayFilename, file } = params
  const apiClient = getApiClient()

  // Step 1: Get presigned URL
  const postSearchParams = {
    display_filename: snakeCase(displayFilename),
    ...config.additionalPostParams,
  }

  return await apiClient
    .post<UploadDocumentToS3Params>(config.endpoint, {
      searchParams: postSearchParams,
    })
    .then(async (response) => {
      const responseData = await response.json()

      // Step 2: Upload file to S3
      return await uploadFileToS3(file, responseData.presignedUrl)
        .then(async () => {
          // Step 3: Finalize document
          return await finalizeDocument({
            endpoint: config.endpoint,
            displayFilename,
            uniqueFilename: responseData.uniqueFilename,
            additionalPatchParams: config.additionalPatchParams,
          })
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error("Error uploading file:", error)
          throw error
        })
    })
}

const finalizeDocument = async ({
  endpoint,
  displayFilename,
  uniqueFilename,
  additionalPatchParams = {},
}: {
  endpoint: string
  displayFilename: string
  uniqueFilename: string
  additionalPatchParams?: Record<string, string | number | boolean>
}) => {
  const apiClient = getApiClient()

  const patchSearchParams = {
    display_filename: displayFilename,
    unique_filename: uniqueFilename,
    ...additionalPatchParams,
  }

  return await apiClient.patch(endpoint, {
    searchParams: patchSearchParams,
  })
}
